import { useState, Fragment, useEffect, useMemo } from 'react'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { colsPresupuesto } from '../../../../data/headersTables'
import baseStyles from './utils/tableStyles'

export const TablePresupuesto = ({ data, inputData, setInputData, expandAll }) => (
  <ExpandableTable
    data={data}
    inputData={inputData}
    setInputData={setInputData}
    expandAll={expandAll}
  />
)

function ExpandableTable({ data, inputData, setInputData, expandAll }) {
  const [expanded, setExpanded] = useState({})
  const MAX_PPTO = 10_000_000_000
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const styles = useMemo(() => {
    if (!isMobile) return baseStyles
    return {
      ...baseStyles,
      container: {
        ...baseStyles.container,
        maxHeight: 420,
        fontSize: '11px'
      },
      table: {
        ...baseStyles.table,
        minWidth: 720,
        fontSize: '11px'
      },
      thead: {
        ...baseStyles.thead,
        fontSize: '11px'
      },
      stickyRow: {
        ...baseStyles.stickyRow,
        top: 32,
        fontSize: '11px'
      },
      rowLevel1: { ...baseStyles.rowLevel1, fontSize: '11px' },
      rowLevel2: { ...baseStyles.rowLevel2, fontSize: '11px' },
      firstColumnCell: {
        ...baseStyles.firstColumnCell,
        width: 160,
        minWidth: 140,
        fontSize: '11px',
        padding: '0.2rem'
      },
      headerFirstColumnCell: {
        ...baseStyles.headerFirstColumnCell,
        width: 160,
        minWidth: 140,
        fontSize: '11px',
        padding: '0.2rem'
      },
      headerSecondColumnCell: {
        ...baseStyles.headerSecondColumnCell,
        width: 140,
        minWidth: 120,
        fontSize: '11px',
        padding: '0.2rem'
      },
      headerCell: {
        ...baseStyles.headerCell,
        width: 140,
        minWidth: 120,
        fontSize: '11px',
        padding: '0.2rem'
      },
      cell: {
        ...baseStyles.cell,
        width: 140,
        minWidth: 120,
        fontSize: '11px',
        padding: '0.2rem'
      },
      inputCell: {
        ...baseStyles.inputCell,
        fontSize: '11px',
        padding: '0.2rem'
      },
      inputCellData: {
        ...baseStyles.inputCellData,
        fontSize: '11px',
        padding: '0.2rem'
      },
      resultadoRow: {
        ...baseStyles.resultadoRow,
        fontSize: '11px'
      }
    }
  }, [isMobile])

  const formatForInput = v => {
    if (v === '' || v === undefined || v === null) return ''
    if (v === '-') return '-' // mientras el usuario escribe el signo
    if (typeof v === 'number' && !isNaN(v)) return v.toLocaleString('es-CL')
    return String(v)
  }

  const onInputChange = (accountCode, col, raw) => {
    // Quita separadores de miles (puntos o comas) y espacios
    const cleaned = raw.replace(/[.,\s]/g, '')

    // Acepta vacío, "-", o números enteros con signo opcional
    if (!/^-?\d*$/.test(cleaned)) return

    if (cleaned === '' || cleaned === '-') {
      setInputData(prev => ({
        ...prev,
        [accountCode]: { ...(prev[accountCode] || {}), [col]: cleaned }
      }))
      return
    }

    let val = parseInt(cleaned, 10)
    // Si el usuario escribió "-" al inicio en el raw original, respétalo
    if (raw.trim().startsWith('-')) val = -Math.abs(val)

    if (Math.abs(val) > MAX_PPTO) val = val < 0 ? -MAX_PPTO : MAX_PPTO

    setInputData(prev => ({
      ...prev,
      [accountCode]: { ...(prev[accountCode] || {}), [col]: val }
    }))
  }

  const toggle = key => setExpanded(e => ({ ...e, [key]: !e[key] }))

  useEffect(() => {
    if (!data.length) return
    const expandNodes = (nodes, acc) => {
      nodes.forEach(n => {
        acc[n.accountCode] = true
        if (n.subRows) expandNodes(n.subRows, acc)
      })
    }
    if (expandAll) {
      const all = {}
      expandNodes(data, all)
      setExpanded(all)
    } else {
      setExpanded({})
    }
  }, [expandAll, data])

  // Si el code empieza por '3-1-1' o '3-2-1' son ingresos (+),
  // resto de subcuentas son egresos (-)
  const getFactor = code => (code.startsWith('3-1-1') || code.startsWith('3-2-1') ? 1 : -1)

  // dentro de ExpandableTable, justo antes de `const projectedTree = useMemo(...`
  const OPERATION_CHILDREN = {
    // accountCode exactos de tus nodos padre:
    '3-1-0-0': [
      // Resultado Operacional
      '3-1-1', // Ingresos de Explotación
      '3-1-2' // Costo de Explotación
    ],
    '3-2-0-0': [
      // Resultado no Operacional
      '3-2-1', // Ingresos Fuera de la Explotación
      '3-2-2', // Gasto de Remuneraciones
      '3-2-3', // Gastos de Administración
      '3-2-4', // Gastos Financieros
      '3-2-5' // Diferencia de Cambio
    ]
  }

  const projectedTree = useMemo(() => {
    const build = node => {
      // 1) histórico real
      const realArr =
        Array.isArray(node.presupuesto) && node.presupuesto.length === 12
          ? node.presupuesto
          : Array(12).fill(0)

      // 2) recursión
      const children = (node.subRows || []).map(build)
      const isLeaf = children.length === 0

      // 3) inicializo
      const projectedPresupuesto = Array(12).fill(0)
      const realPresupuesto = Array(12).fill(0)

      if (isLeaf) {
        // hoja → uso sólo inputData o 0
        for (let i = 0; i < 12; i++) {
          const col = colsPresupuesto[i + 2]
          const u = inputData[node.accountCode]?.[col]
          projectedPresupuesto[i] = typeof u === 'number' ? u : 0
          realPresupuesto[i] = realArr[i]
        }
      } else {
        // padre → ¿es operacional o genérico?
        const hijosKeys = OPERATION_CHILDREN[node.accountCode]
        for (let i = 0; i < 12; i++) {
          if (hijosKeys) {
            // solo esos hijos, con signo según tipo
            const hijos = children.filter(c =>
              hijosKeys.some(pref => c.accountCode.startsWith(pref))
            )
            realPresupuesto[i] = hijos.reduce(
              (s, c) => s + getFactor(c.accountCode) * c.realPresupuesto[i],
              0
            )
            projectedPresupuesto[i] = hijos.reduce(
              (s, c) => s + getFactor(c.accountCode) * c.projectedPresupuesto[i],
              0
            )
          } else {
            // genérico → suma de todos
            realPresupuesto[i] = children.reduce((s, c) => s + c.realPresupuesto[i], 0)
            projectedPresupuesto[i] = children.reduce((s, c) => s + c.projectedPresupuesto[i], 0)
          }
        }
      }

      // 4) totales
      const realTotal = realPresupuesto.reduce((s, x) => s + x, 0)
      const projectedTotal = projectedPresupuesto.reduce((s, x) => s + x, 0)

      return {
        ...node,
        subRows: node.subRows?.length ? children : [],
        realPresupuesto,
        projectedPresupuesto,
        realTotal,
        projectedTotal
      }
    }

    // construyo el árbol completo
    return data.map(build)
  }, [data, inputData])

  const projectedMap = useMemo(() => {
    const m = {}
    const walk = rows =>
      rows.forEach(n => {
        m[n.accountCode] = n
        if (n.subRows) walk(n.subRows)
      })
    walk(projectedTree)
    return m
  }, [projectedTree])

  const fmt = num => (typeof num === 'number' && !isNaN(num) ? num.toLocaleString('es-CL') : '')

  const renderRows = (rows, lvl = 0) =>
    rows.map(r => {
      const key = r.accountCode
      const isLeaf = !r.subRows || r.subRows.length === 0
      const styleRow =
        lvl === 0 ? styles.stickyRow : lvl === 1 ? styles.rowLevel1 : styles.rowLevel2

      const proj = projectedMap[key] || {
        realPresupuesto: Array(12).fill(0),
        projectedPresupuesto: Array(12).fill(0),
        realTotal: 0,
        projectedTotal: 0
      }

      return (
        <Fragment key={key}>
          <tr style={{ ...styleRow, borderBottom: isLeaf ? '1px solid rgb(var(--border))' : 'none' }}>
            <td style={{ ...styles.firstColumnCell, backgroundColor: styleRow.backgroundColor }}>
              {r.subRows?.length > 0 && (
                <button style={styles.button} onClick={() => toggle(key)}>
                  {expanded[key] ? (
                    <KeyboardArrowUpIcon fontSize='small' />
                  ) : (
                    <KeyboardArrowDownIcon fontSize='small' />
                  )}
                </button>
              )}
              {r.name}
            </td>

            {/* Total REAL, PPTO, DIF */}
            {(() => {
              const realT = proj.realTotal
              const pptoT = proj.projectedTotal

              const difT = pptoT === 0 ? 0 : ((realT - pptoT) / pptoT) * 100
              return [
                <td key={`total-real-${key}`} style={{ ...styles.cell, textAlign: 'center' }}>
                  {fmt(realT)}
                </td>,
                <td key={`total-ppto-${key}`} style={{ ...styles.cell, textAlign: 'center' }}>
                  {fmt(pptoT)}
                </td>,
                <td key={`total-dif-${key}`} style={{ ...styles.cell, textAlign: 'center' }}>
                  {difT.toFixed(2)}%
                </td>
              ]
            })()}

            {/* Por mes: REAL, PPTO (input o suma), DIF */}
            {colsPresupuesto.slice(2).flatMap((col, i) => {
              const realM = proj.realPresupuesto[i]
              const pptoM = proj.projectedPresupuesto[i]
              const difM = pptoM === 0 ? 0 : ((realM - pptoM) / pptoM) * 100

              return [
                <td key={`m-real-${key}-${i}`} style={{ ...styles.cell, textAlign: 'center' }}>
                  {fmt(realM)}
                </td>,
                <td key={`m-ppto-${key}-${i}`} style={{ ...styles.cell, textAlign: 'center' }}>
                  {isLeaf ? (
                    <input
                      type='text'
                      inputMode='numeric'
                      pattern='[0-9]*'
                      style={{ ...styles.inputCell, textAlign: 'center' }}
                      value={formatForInput(inputData[key]?.[col])}
                      onChange={e => onInputChange(key, col, e.target.value)}
                      title={`Máximo permitido: ${MAX_PPTO.toLocaleString('es-CL')}`}
                    />
                  ) : (
                    fmt(pptoM)
                  )}
                </td>,
                <td key={`m-dif-${key}-${i}`} style={{ ...styles.cell, textAlign: 'center' }}>
                  {difM.toFixed(2)}%
                </td>
              ]
            })}
          </tr>
          {expanded[key] && r.subRows?.length > 0 && renderRows(r.subRows, lvl + 1)}
        </Fragment>
      )
    })

  const renderResultado = () => {
    // Buscar un nodo en todo el árbol calculado
    const findNode = predicate => {
      const stack = [...projectedTree]
      while (stack.length) {
        const n = stack.pop()
        if (predicate(n)) return n
        if (n.subRows && n.subRows.length) stack.push(...n.subRows)
      }
      return null
    }

    // Intentar por código (varias opciones) o por nombre
    const posiblesOp = ['3-1-0-0', '3-1-0', '3-1']
    const posiblesNoOp = ['3-2-0-0', '3-2-0', '3-2']

    const operacional =
      posiblesOp.map(c => projectedMap[c]).find(Boolean) ||
      findNode(n => /resultado\s*operacional/i.test(n.name || ''))

    const noOperacional =
      posiblesNoOp.map(c => projectedMap[c]).find(Boolean) ||
      findNode(n => /resultado\s*no\s*operacional/i.test(n.name || ''))

    if (!operacional || !noOperacional) {
      console.warn('No encontré los nodos para Resultado.', {
        disponibles: Object.keys(projectedMap)
      })
      return (
        <tr style={styles.resultadoRow}>
          <td style={styles.firstColumnCell}>Resultado</td>
          <td style={styles.cell}>{fmt(0)}</td>
          <td style={styles.cell}>{fmt(0)}</td>
          <td style={styles.cell}>0.00%</td>
          {colsPresupuesto.slice(2).flatMap((_, i) => [
            <td key={`res-r-${i}`} style={styles.cell}>
              {fmt(0)}
            </td>,
            <td key={`res-p-${i}`} style={styles.cell}>
              {fmt(0)}
            </td>,
            <td key={`res-d-${i}`} style={styles.cell}>
              0.00%
            </td>
          ])}
        </tr>
      )
    }

    // Helpers
    const sumArrays = (a = [], b = []) => a.map((v, i) => (v || 0) + (b[i] || 0))

    // REAL por mes (ya viene con signos correctos del projectedTree)
    const realMeses = sumArrays(operacional.realPresupuesto, noOperacional.realPresupuesto)

    // ⚠️ CAMBIO CLAVE: TOTAL REAL se deriva de los meses (no de realTotal de cada nodo)
    const realTotal = realMeses.reduce((s, x) => s + x, 0)

    // PPTO: no se toca (deja tu lógica como suma directa)
    const pptoMeses = sumArrays(
      operacional.projectedPresupuesto,
      noOperacional.projectedPresupuesto
    )
    const pptoTotal = operacional.projectedTotal + noOperacional.projectedTotal

    const difTotal = pptoTotal === 0 ? 0 : ((realTotal - pptoTotal) / pptoTotal) * 100
    const difMeses = realMeses.map((r, i) => {
      const p = pptoMeses[i]
      return p === 0 ? 0 : ((r - p) / p) * 100
    })

    return (
      <tr style={styles.resultadoRow}>
        <td style={styles.firstColumnCell}>Resultado</td>
        <td style={styles.cell}>{fmt(realTotal)}</td>
        <td style={styles.cell}>{fmt(pptoTotal)}</td>
        <td style={styles.cell}>{difTotal.toFixed(2)}%</td>
        {colsPresupuesto.slice(2).flatMap((_, i) => [
          <td key={`res-r-${i}`} style={styles.cell}>
            {fmt(realMeses[i])}
          </td>,
          <td key={`res-p-${i}`} style={styles.cell}>
            {fmt(pptoMeses[i])}
          </td>,
          <td key={`res-d-${i}`} style={styles.cell}>
            {difMeses[i].toFixed(2)}%
          </td>
        ])}
      </tr>
    )
  }

  return (
    <div style={styles.container}>
      <table style={styles.table}>
        <thead style={styles.thead}>
          <tr>
            <th rowSpan={2} style={styles.headerFirstColumnCell}>
              Cuentas
            </th>
            <th colSpan={3} style={styles.headerCell}>
              Total
            </th>
            {colsPresupuesto.slice(2).map(c => (
              <th key={c} colSpan={3} style={styles.headerCell}>
                {c}
              </th>
            ))}
          </tr>
          <tr>
            <th style={styles.headerCell}>REAL</th>
            <th style={styles.headerCell}>PPTO</th>
            <th style={styles.headerCell}>DIF</th>
            {colsPresupuesto.slice(2).flatMap(c => [
              <th key={`${c}-r`} style={styles.headerCell}>
                REAL
              </th>,
              <th key={`${c}-p`} style={styles.headerCell}>
                PPTO
              </th>,
              <th key={`${c}-d`} style={styles.headerCell}>
                DIF
              </th>
            ])}
          </tr>
        </thead>
        <tbody>
          {renderRows(data)}
          {renderResultado()}
        </tbody>
      </table>
    </div>
  )
}

export default ExpandableTable
